/**
 * The tiffin order being built.
 *
 * Like the donation draft, it holds only what the customer has chosen — the
 * meal, the box, the persons, the route and who to reach — and never a total.
 * The price lives in the server quote, keyed to exactly this selection, so a
 * stale or tampered figure has nowhere to be kept.
 *
 * Quantities are per box. Persons multiply the box: two Pepsis in a box for
 * three persons is six Pepsis, and the review screen says so. The plan
 * multiplies it again: the same meal, at the same time, on each day the
 * schedule picks in the run of days from the first.
 */

import { create } from 'zustand';

import {
  selectedLines,
  tiffinQuoteMatches,
  type CreateTiffinOrderRequest,
  type DeliveryAddress,
  type FulfilmentType,
  type Money,
  type ServiceSchedule,
  type ServiceSlot,
  type TiffinCustomer,
  type TiffinQuote,
  type TiffinQuoteRequest,
} from '@mfa/domain';

export interface TiffinDraft {
  serviceDate: string | null;
  serviceSlot: ServiceSlot | null;
  /** menuItemId → quantity in one box. */
  quantities: Record<string, number>;
  /** One by default: most tiffins are for the person ordering. */
  persons: number;
  /**
   * Consecutive days from `serviceDate`. Null until the kitchen's minimum is
   * known; the details step starts it there.
   */
  days: number | null;
  /** Which of those days it comes. Null until the kitchen's offer is known. */
  schedule: ServiceSchedule | null;
  fulfilmentType: FulfilmentType | null;
  address: DeliveryAddress;
  customer: TiffinCustomer;
  note: string;
  paymentMethodCode: string | null;
  walletMsisdn: string | null;
  idempotencyKey: string | null;
  quote: TiffinQuote | null;
}

interface TiffinStore extends TiffinDraft {
  setDate: (date: string) => void;
  setSlot: (slot: ServiceSlot) => void;
  setQuantity: (menuItemId: string, quantity: number) => void;
  setPersons: (persons: number) => void;
  setDays: (days: number) => void;
  setSchedule: (schedule: ServiceSchedule) => void;
  setFulfilment: (type: FulfilmentType) => void;
  setAddress: (patch: Partial<DeliveryAddress>) => void;
  setCustomer: (patch: Partial<TiffinCustomer>) => void;
  setNote: (note: string) => void;
  setPaymentMethod: (code: string) => void;
  setWalletMsisdn: (msisdn: string | null) => void;
  setQuote: (quote: TiffinQuote | null) => void;
  /** Drop items that are not on the chosen meal's menu. */
  keepOnly: (menuItemIds: string[]) => void;
  ensureIdempotencyKey: () => string;
  reset: () => void;
  itemCount: () => number;
  /** The server quote, only while it still describes this selection. */
  authoritativeTotal: () => Money | null;
  quoteRequest: () => TiffinQuoteRequest | null;
}

export const EMPTY_ADDRESS: DeliveryAddress = {
  addressLine: '',
  area: null,
  city: '',
  landmark: null,
};

const EMPTY: TiffinDraft = {
  serviceDate: null,
  serviceSlot: null,
  quantities: {},
  persons: 1,
  days: null,
  schedule: null,
  fulfilmentType: null,
  address: EMPTY_ADDRESS,
  customer: { fullName: '', mobileNumber: '', email: null },
  note: '',
  paymentMethodCode: null,
  walletMsisdn: null,
  idempotencyKey: null,
  quote: null,
};

/** RFC 4122 v4 without pulling in a uuid dependency. */
function uuid(): string {
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const useTiffinStore = create<TiffinStore>((set, get) => ({
  ...EMPTY,

  setDate: (serviceDate) => set({ serviceDate }),
  setSlot: (serviceSlot) => set({ serviceSlot }),

  setQuantity: (menuItemId, quantity) =>
    set((state) => {
      const next = { ...state.quantities };
      if (quantity > 0) next[menuItemId] = quantity;
      else delete next[menuItemId];
      return { quantities: next };
    }),

  setPersons: (persons) => set({ persons: Math.max(1, Math.trunc(persons)) }),
  setDays: (days) => set({ days: Math.max(1, Math.trunc(days)) }),
  setSchedule: (schedule) => set({ schedule }),
  setFulfilment: (fulfilmentType) => set({ fulfilmentType }),
  setAddress: (patch) => set((state) => ({ address: { ...state.address, ...patch } })),
  setCustomer: (patch) => set((state) => ({ customer: { ...state.customer, ...patch } })),
  setNote: (note) => set({ note }),
  setPaymentMethod: (paymentMethodCode) => set({ paymentMethodCode }),
  setWalletMsisdn: (walletMsisdn) => set({ walletMsisdn }),
  setQuote: (quote) => set({ quote }),

  keepOnly: (menuItemIds) =>
    set((state) => {
      const allowed = new Set(menuItemIds);
      const next = Object.fromEntries(
        Object.entries(state.quantities).filter(([id]) => allowed.has(id)),
      );
      return Object.keys(next).length === Object.keys(state.quantities).length
        ? {}
        : { quantities: next };
    }),

  ensureIdempotencyKey: () => {
    const existing = get().idempotencyKey;
    if (existing) return existing;
    const key = uuid();
    set({ idempotencyKey: key });
    return key;
  },

  reset: () => set({ ...EMPTY }),

  itemCount: () => Object.values(get().quantities).reduce((sum, quantity) => sum + quantity, 0),

  authoritativeTotal: () => {
    const state = get();
    if (!state.quote || state.days === null || !state.schedule) return null;
    const selection = { ...state, days: state.days, schedule: state.schedule };
    if (!tiffinQuoteMatches(state.quote, selection)) return null;
    return state.quote.totalAmount;
  },

  quoteRequest: () => {
    const state = get();
    const items = selectedLines(state.quantities);
    if (
      !state.serviceDate ||
      !state.serviceSlot ||
      state.days === null ||
      !state.schedule ||
      items.length === 0 ||
      !state.fulfilmentType
    ) {
      return null;
    }
    const delivery = state.fulfilmentType === 'DOORSTEP_DELIVERY';
    if (delivery && !state.address.city.trim()) return null;
    return {
      serviceDate: state.serviceDate,
      serviceSlot: state.serviceSlot,
      persons: state.persons,
      days: state.days,
      schedule: state.schedule,
      items,
      fulfilmentType: state.fulfilmentType,
      ...(delivery ? { deliveryCity: state.address.city.trim() } : {}),
    };
  },
}));

/** Build the order payload. Throws if the draft is incomplete. */
export function toCreateTiffinRequest(
  draft: ReturnType<typeof useTiffinStore.getState>,
): CreateTiffinOrderRequest {
  const request = draft.quoteRequest();
  if (!request || !draft.quote || !draft.paymentMethodCode) {
    throw new Error('The tiffin order is incomplete.');
  }
  const delivery = request.fulfilmentType === 'DOORSTEP_DELIVERY';
  return {
    quoteToken: draft.quote.quoteToken,
    serviceDate: request.serviceDate,
    serviceSlot: request.serviceSlot,
    persons: request.persons,
    days: request.days,
    schedule: request.schedule,
    items: request.items,
    fulfilmentType: request.fulfilmentType,
    customer: {
      fullName: draft.customer.fullName.trim(),
      mobileNumber: draft.customer.mobileNumber.trim(),
      email: draft.customer.email?.trim() || null,
    },
    address: delivery
      ? {
          addressLine: draft.address.addressLine.trim(),
          area: draft.address.area?.trim() || null,
          city: draft.address.city.trim(),
          landmark: draft.address.landmark?.trim() || null,
        }
      : null,
    note: draft.note.trim() || null,
    paymentMethodCode: draft.paymentMethodCode,
    idempotencyKey: draft.ensureIdempotencyKey(),
  };
}
